import { getFileUploadUrl } from "../utils/get-file-upload-url.js";
import { uploadFile } from "../utils/upload-file.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// src/modules/Files/hooks/useFileUploader.ts
var defaultOpts = {
	immediateUpload: true,
	requireAuth: true
};
function useFileUploader(options = defaultOpts) {
	const opts = {
		...defaultOpts,
		...options
	};
	const [filesById, setFilesById] = useState({});
	const filesByIdRef = useRef({});
	const refreshRef = useRef(() => null);
	const local = useRef({ mounted: true });
	refreshRef.current = () => {
		if (local.current.mounted) setFilesById({ ...filesByIdRef.current });
	};
	useEffect(() => {
		local.current.mounted = true;
		return () => {
			local.current.mounted = false;
			Object.values(filesByIdRef).forEach((file) => {
				file.uploadAbortPromise?.();
				URL.revokeObjectURL(file.imagePreviewUrl || "");
			});
		};
	}, []);
	const uploadSingleFile = useCallback(async (file) => {
		const uploadState = uploadFile(file, (uploadProgress) => {
			filesByIdRef.current[file.id].uploadProgress = uploadProgress;
			refreshRef.current();
		});
		if (!uploadState) return;
		const { abortPromise, uploadPromise } = uploadState;
		file.status = "uploading";
		file.uploadAbortPromise = abortPromise;
		file.uploadPromise = uploadPromise;
		filesByIdRef.current[file.id] = { ...file };
		refreshRef.current();
		await uploadPromise.then(() => {
			file.status = "uploaded";
			filesByIdRef.current[file.id] = { ...file };
			refreshRef.current();
		}).catch((err) => {
			console.error("useFileUploader()#uploadFiles: error uploading file", file, err);
			file.status = "upload-error";
			filesByIdRef.current[file.id] = { ...file };
		}).finally(() => {
			refreshRef.current();
		});
	}, []);
	const handleNewFileAdded = useCallback((file) => {
		if (file.type.includes("image")) filesByIdRef.current[file.id].imagePreviewUrl = URL.createObjectURL(file.file);
		refreshRef.current();
		getFileUploadUrl(file, { requireAuth: opts.requireAuth }).then(({ accessUrl, uploadMethod, uploadUrl }) => {
			if (!local.current.mounted) return;
			filesByIdRef.current[file.id].accessUrl = accessUrl;
			filesByIdRef.current[file.id].status = "ready-to-upload";
			filesByIdRef.current[file.id].uploadMethod = uploadMethod;
			filesByIdRef.current[file.id].uploadUrl = uploadUrl;
			return filesByIdRef.current[file.id];
		}).then((file) => {
			if (opts.immediateUpload && file) uploadSingleFile(file);
		}).catch((err) => {
			if (!local.current.mounted) return;
			console.error("useFileUploader()#handleNewFileAdded: getFileUploadUrl() error", err);
			filesByIdRef.current[file.id].status = "processing-error";
		}).finally(() => {
			refreshRef.current();
		});
	}, [opts.requireAuth, opts.immediateUpload]);
	const removeFile = useCallback((fileId) => {
		const file = filesByIdRef.current[fileId];
		if (file) {
			file?.uploadAbortPromise?.();
			URL.revokeObjectURL(file.imagePreviewUrl || "");
			delete filesByIdRef.current[fileId];
			refreshRef.current();
		}
	}, [filesById]);
	const removeAllFiles = useCallback(() => {
		for (const file of Object.values(filesById)) {
			file?.uploadAbortPromise?.();
			URL.revokeObjectURL(file.imagePreviewUrl || "");
		}
		filesByIdRef.current = {};
		refreshRef.current();
	}, [filesById]);
	/**
	* Pass the "files" list from the <input type="file" /> Element to this callback
	*/
	const addFiles = useCallback(async (fileList) => {
		const files = Array.from(fileList);
		for (let file of files) {
			if (!file.type) file = new File([file], file.name, { type: "application/unknown" });
			let newFile = {
				file,
				id: Math.random().toString(),
				name: file.name,
				size: file.size,
				status: "processing",
				type: file.type
			};
			if (opts.filePreprocessor) {
				try {
					newFile.status = "preprocessing";
					newFile = await opts.filePreprocessor(newFile);
				} catch (err) {
					console.error("useFileUploader() preprocessing error", err);
					newFile.status = "preprocessing-error";
				}
				newFile.size = newFile.file.size;
				newFile.type = newFile.file.type;
			}
			if (opts.canAddFile) {
				if (!opts.canAddFile(newFile, Object.values(filesByIdRef))) {
					console.info("useFileUploader() file add skipped", newFile);
					continue;
				}
			}
			filesByIdRef.current[newFile.id] = newFile;
			handleNewFileAdded(newFile);
		}
	}, []);
	const replaceAllFiles = useCallback((fileList) => {
		removeAllFiles();
		addFiles(fileList);
	}, []);
	const retryFileUpload = useCallback((fileId) => {
		const fileToRetry = filesByIdRef.current[fileId];
		if (!fileToRetry) {
			console.error(`retryFileUpload(fileId: ${fileId}) err - file not found in current list`);
			return;
		}
		if (fileToRetry.uploadUrl) uploadSingleFile(fileToRetry);
	}, []);
	const uploadFiles = useCallback(async () => {
		const getNextFile = () => Object.values(filesByIdRef.current).filter(({ status }) => status === "ready-to-upload")[0];
		let file = getNextFile();
		while (file) {
			if (file.status !== "ready-to-upload") continue;
			if (file) try {
				await uploadSingleFile(file);
			} catch (err) {
				filesByIdRef[file.id].status = "upload-error";
				console.error(`await uploadSingleFile(file) errored`, err);
			}
			file = getNextFile();
		}
		refreshRef.current();
	}, []);
	const [files, uploadInProgress, uploadProgressStatus, allUploadsComplete] = useMemo(() => {
		const files = Object.values(filesByIdRef.current);
		let totalUploadProgressAggregate = 0;
		let totalForUpload = 0;
		let uploadInProgress = false;
		const allUploadsComplete = files.reduce((acc, file) => {
			if (file.status === "uploading") uploadInProgress = true;
			switch (file.status) {
				case "ready-to-upload":
				case "preprocessing":
				case "processing":
				case "uploading":
					totalUploadProgressAggregate += file.uploadProgress || 0;
					totalForUpload++;
					break;
				case "uploaded":
					totalUploadProgressAggregate += 100;
					totalForUpload++;
			}
			if (!acc || file.status !== "uploaded") return false;
			return acc;
		}, true);
		const uploadProgressStatus = +((totalUploadProgressAggregate / totalForUpload).toFixed(0) || 100);
		return [
			files,
			uploadInProgress,
			uploadProgressStatus,
			allUploadsComplete
		];
	}, [filesById]);
	return {
		addFiles,
		allUploadsComplete,
		files,
		removeAllFiles,
		removeFile,
		replaceAllFiles,
		retryFileUpload,
		uploadFiles,
		uploadInProgress,
		/** Total progress of all currently uploading files */
		uploadProgressStatus
	};
}
export { useFileUploader };

//# sourceMappingURL=useFileUploader.js.map